"use client";
import React from "react";
import { motion } from "framer-motion";
import { Sun, Home, Settings, Bolt, ShieldCheck, Wrench } from "lucide-react";

const installationSteps = [
  {
    icon: Home,
    title: "Site Assessment & Feasibility Study",
    description: "Our engineers evaluate your rooftop’s structural integrity, solar potential, and energy consumption patterns to design a customized rooftop solar power system that meets your needs."
  },
  {
    icon: Settings,
    title: "System Design & Engineering",
    description: "Using advanced analytics and engineering tools, we develop a tailored solar panel installation plan, ensuring optimal panel placement for maximum sunlight absorption and energy output."
  },
  {
    icon: Bolt,
    title: "Procurement & Quality Assurance",
    description: "We source high-quality solar panels, inverters, and mounting structures from trusted manufacturers, ensuring durability and efficiency for long-term performance."
  },
  {
    icon: Sun,
    title: "Installation & Integration",
    description: "Our certified professionals carry out rooftop solar installation with precision, ensuring secure mounting, proper wiring, and compliance with safety standards."
  },
  {
    icon: ShieldCheck,
    title: "Grid Connection & Commissioning",
    description: "After installation, we handle all permits and approvals. Once commissioned, we monitor performance and offer ongoing maintenance services."
  },
  {
    icon: Wrench,
    title: "Maintenance & Long-Term Support",
    description: "We provide ongoing maintenance, monitoring, and support to ensure your rooftop solar system operates at peak efficiency for 25+ years."
  }
];

const SolarInstallation = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: false }}
      className="flex flex-col bg-quad lg:justify-evenly lg:h-auto py-[4rem] px-[1.5rem] sm:px-[3rem] lg:px-[6rem]"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: false }}
        className="flex flex-col gap-3 mb-6"
      >
        <div className="text-pri text-desc-size2 flex items-center gap-2">
          <Sun size={20} />
          <span>How Satyanam Solar Installs Solar Rooftop?</span>
        </div>
        <div className="text-text1 text-title-size3 lg:text-title-size2 md:w-[60vw] leading-[1]">
          Our team maximizes each solar roof’s energy, efficiency, and durability.
        </div>
      </motion.div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16 justify-items-center">
        {installationSteps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: index * 0.2 }}
            viewport={{ once: false }}
            className="relative flex flex-col items-start gap-4 bg-white p-8 rounded-2xl shadow-sm w-full overflow-hidden group"
          >
            <div className="absolute inset-0 bg-[#40204A] origin-right transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-in-out" />

            <div className="relative z-10 flex w-14 h-14 rounded-md bg-[#40204A] items-center justify-center group-hover:bg-white">
              <step.icon size={24} className="text-white group-hover:text-[#40204A]" />
            </div>
            <div className="relative z-10 text-color1 text-desc-size1 font-semibold group-hover:text-white">
              {step.title}
            </div>
            <div className="relative z-10 text-color3 text-desc-size2 group-hover:text-white">
              {step.description}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default SolarInstallation;
